import { StyledBorderButton, StyledButton } from "@components";
import { IAccount, ICustomerDetail } from "@interfaces";
import { PATH_MY_ACCOUNT } from "@routes";
import { breakpoint, theme } from "@utilities";
import { isPlaceholderPhone } from "../signupOfferConfig";
import { resolveTravellerDetails } from "../travellerDetails";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";

/** Lookup miss from getCountryName — shown to nobody, filtered out on sight. */
const COUNTRY_NOT_FOUND = "Not Found";

export interface Suggestion {
    first_name: string | null;
    last_name: string | null;
    phone_number: string | number | null;
    country_code: string | null;
    email: string | null;
    id: string;
    quoteId?: number;
    customers?: ICustomerDetail[];
}

interface TravellerSuggestionsBottomSheetProps {
    isOpen: boolean;
    onClose: () => void;
    suggestions: ICustomerDetail[];
    selectedSuggestion: Suggestion | null;
    onSelect: (suggestion: Suggestion) => void;
    onAdd: () => void;
    isAdding?: boolean;
    onCreateNew: () => void;
    countriesData: any[];
    shouldAskPersonalDetails: boolean;
    /**
     * Marks a saved traveller whose stored details are sign-up stand-ins. Those
     * cannot be added to a booking as they are, so the row is flagged and the
     * primary button sends the visitor to the form to correct them instead.
     */
    needsDetailReview?: (traveller: ICustomerDetail) => boolean;
    /**
     * The signed-in account, used as the live source for the account holder's own
     * row. Its nested copy on the suggestion is a snapshot and can lag a
     * correction made elsewhere in the checkout.
     */
    account?: IAccount | null;
}

interface StyledSuggestionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    isSuggestionClicked: boolean;
    disabled?: boolean;
}

export const TravellerSuggestionsBox = ({
    isOpen,
    onClose,
    suggestions,
    selectedSuggestion,
    onSelect,
    onAdd,
    isAdding = false,
    onCreateNew,
    countriesData,
    shouldAskPersonalDetails,
    needsDetailReview,
    account,
}: TravellerSuggestionsBottomSheetProps) => {
    if (!isOpen) return null;

    const getCountryName = (targetCode: string | undefined) =>
        _.get(_.find(countriesData, { countryCode: targetCode }), "name", COUNTRY_NOT_FOUND);

    // The selected row is matched back to its full record: `Suggestion` is a
    // flattened subset and does not carry the fields the check needs.
    const selectedNeedsReview = (() => {
        if (!selectedSuggestion || !needsDetailReview) return false;
        const full = _.find(suggestions, { id: selectedSuggestion.id });
        return full ? needsDetailReview(full) : false;
    })();
    const navigate = useNavigate();

    return (
        <>
            <Overlay onClick={onClose} />
            <BottomSheet onClick={(e) => e.stopPropagation()}>
                {shouldAskPersonalDetails ? (
                    <div className="text-container">
                        <h2>Your Personal details are missing</h2>
                        <p>
                            You haven't entered your personal details yet. Please fill them in to
                            proceed.
                        </p>
                        <StyledButton
                            $borderRadius={theme.borderRadius.small}
                            color={theme.gradients.button}
                            onClick={() => navigate(PATH_MY_ACCOUNT)}
                        >
                            Add my personal details
                        </StyledButton>
                    </div>
                ) : (
                    <>
                        <div className="details-container">
                            <div className="header">
                                <h2>Suggestions</h2>
                                <CloseButton onClick={onClose}>
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M18 6L6 18M6 6L18 18"
                                            stroke="#080914"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </CloseButton>
                            </div>
                            <ScrollBox>
                                {suggestions.length === 0 ? (
                                    <NoSuggestions>
                                        <p>You don't have any suggestions</p>
                                    </NoSuggestions>
                                ) : (
                                    _.map(suggestions, (user) => {
                                        const {
                                            firstName,
                                            lastName,
                                            phoneNumber,
                                            countryCode,
                                            email,
                                        } = resolveTravellerDetails(user, account);
                                        const needsReview = needsDetailReview?.(user) ?? false;

                                        // Only real details belong in the
                                        // subtitle. The stand-in phone number is
                                        // dropped outright; the country goes too
                                        // on a row that needs review, because its
                                        // code came from the same stand-in set
                                        // rather than from the visitor. An
                                        // unresolvable code renders as "Not Found",
                                        // which is noise either way.
                                        const countryName =
                                            countryCode && !needsReview
                                                ? getCountryName(countryCode)
                                                : undefined;
                                        const subtitle = [
                                            email,
                                            isPlaceholderPhone(phoneNumber)
                                                ? undefined
                                                : phoneNumber,
                                            countryName === COUNTRY_NOT_FOUND
                                                ? undefined
                                                : countryName,
                                        ]
                                            .filter(Boolean)
                                            .join(", ");

                                        return (
                                            <div className="details" key={user.id}>
                                                <span>
                                                    <StyledSuggestionButton
                                                        type="button"
                                                        isSuggestionClicked={
                                                            selectedSuggestion?.id === user.id
                                                        }
                                                        onClick={() =>
                                                            onSelect({
                                                                first_name: firstName,
                                                                last_name: lastName,
                                                                phone_number: Number(phoneNumber),
                                                                country_code: countryCode,
                                                                email: email,
                                                                id: user.id,
                                                            })
                                                        }
                                                    >
                                                        <div />
                                                    </StyledSuggestionButton>
                                                </span>
                                                <div>
                                                    <h1>
                                                        {firstName} {lastName}{" "}
                                                        {user.is_primary && "(you)"}
                                                        {needsReview && (
                                                            <ReviewBadge>
                                                                Details needed
                                                            </ReviewBadge>
                                                        )}
                                                    </h1>
                                                    {subtitle && <p>{subtitle}</p>}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </ScrollBox>
                        </div>
                        <div className="styled-buttons">
                            <BorderButton
                                $borderRadius={theme.borderRadius.small}
                                color="transparent"
                                $textColor={theme.colors.button_text1}
                                onClick={onCreateNew}
                            >
                                Create New
                            </BorderButton>
                            <NormalStyledButton
                                $borderRadius={theme.borderRadius.small}
                                color={theme.colors.main}
                                onClick={onAdd}
                                disabled={!selectedSuggestion || isAdding}
                            >
                                {isAdding
                                    ? "Adding..."
                                    : selectedNeedsReview
                                      ? "Edit details"
                                      : "Add to booking"}
                            </NormalStyledButton>
                        </div>
                    </>
                )}
            </BottomSheet>
        </>
    );
};

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    .header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 20px !important;
    }

    /* Mobile touch improvements */
    @media (max-width: 768px) {
        // min-height: 44px;
        min-width: 44px;
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
    }

    /* Desktop hover effect */
    ${breakpoint.breakTablet`
        transition: opacity 0.2s ease;
        &:hover {
            opacity: 0.7;
        }
    `}
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const Overlay = styled.div`
    display: none;
    ${[breakpoint.breakMobile, breakpoint.breakSmall].map(
        (breakpointFn) => breakpointFn`
    display: block;`,
    )}
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 10;
    cursor: pointer;

    /* Force show on mobile devices */
    @media (max-width: 768px) {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
    }

    /* Safari-specific mobile support */
    @supports (-webkit-touch-callout: none) {
        @media (max-width: 768px) {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            pointer-events: auto !important;
        }
    }

    /* Additional mobile breakpoints */
    @media (max-width: 480px) {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
    }

    @media (max-width: 375px) {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
    }

    /* Force mobile layout on small screens */
    @media (max-width: 767px) {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        /* Safari mobile specific properties */
        -webkit-overflow-scrolling: touch;
        -webkit-transform: translateZ(0);
        transform: translateZ(0);
    }

    /* Desktop view - ensure overlay is visible and clickable */
    ${breakpoint.breakTablet`
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        z-index: 10;
    `}
`;

const BottomSheet = styled.div`
    display: none;
    ${[breakpoint.breakMobile, breakpoint.breakSmall, breakpoint.breakMobileMedium].map(
        (breakpointFn) => breakpointFn`
        display: block;
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        z-index: 15;
        background: #fff;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
        animation: ${slideUp} 0.3s ease-out;
        padding: 38px 16px 30px 16px;

        .text-container {
            display: flex;
            flex-direction: column;
            /* Use margin instead of gap for Safari mobile compatibility */
            text-align: center;
            h2 {
                font-size: 20px;
                font-weight: 600;
                margin-bottom: 20px;
            }
            p {
                margin-bottom: 20px;
            }
        }

        .details-container{
            display: flex;
            flex-direction: column;
            /* Use margin instead of gap for Safari mobile compatibility */
            margin-bottom: 30px;
            h2 {
                font-size: 20px;
                font-weight: 600;
                margin-bottom: 20px !important;
            }
            .details {
                display : flex;
                align-items : center;
                gap : 16px;
                margin-bottom: 20px;
                &:last-child {
                    margin-bottom: 0;
                }
                div{
                    display : flex;
                    flex-direction : column;
                    /* Use margin instead of gap for Safari mobile compatibility */
                    h1{
                        font-size : 24px;
                        font-weight : 600;
                        margin-bottom: 6px;
                    }
                    p{
                        font-size : 12px;
                        color : #6E7375;
                        margin-bottom: 0;
                    }
                }
            }
            .header {
                display: flex;
                justify-content: space-between;
            }
            
        }
        .styled-buttons {
            display : flex;
            gap : 14px;
        }
    `,
    )}

    /* Force show on mobile devices */
    @media (max-width: 768px) {
        display: block !important;
        position: fixed !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        top: auto !important;
        transform: none !important;
        width: 100% !important;
        max-width: 100% !important;
        z-index: 15;
        background: #fff;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
        border-bottom-left-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
        animation: ${slideUp} 0.3s ease-out;
        padding: 38px 16px 30px 16px;
        /* Safari mobile specific properties */
        -webkit-overflow-scrolling: touch;
        -webkit-transform: translateZ(0);
        transform: translateZ(0);
        /* Safari mobile appearance fixes */
        -webkit-appearance: none;
        -webkit-border-radius: 16px 16px 0 0;
        border-radius: 16px 16px 0 0;
        /* Ensure proper Safari mobile rendering */
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
        -webkit-perspective: 1000px;
        perspective: 1000px;
        /* Force bottom sheet layout */
        height: auto !important;
        min-height: auto !important;

        .text-container {
            h2 {
                margin-bottom: 20px;
                /* Safari mobile text rendering */
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
                font-smoothing: antialiased;
            }
            p {
                margin-bottom: 20px;
                /* Safari mobile text rendering */
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
                font-smoothing: antialiased;
            }
        }

        .details-container {
            h2 {
                margin-bottom: 20px;
                /* Safari mobile text rendering */
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
                font-smoothing: antialiased;
            }
            .details {
                margin-bottom: 20px;
                &:last-child {
                    margin-bottom: 0;
                }
                div h1 {
                    margin-bottom: 6px;
                    /* Safari mobile text rendering */
                    -webkit-font-smoothing: antialiased;
                    -moz-osx-font-smoothing: grayscale;
                    font-smoothing: antialiased;
                }
                div p {
                    /* Safari mobile text rendering */
                    -webkit-font-smoothing: antialiased;
                    -moz-osx-font-smoothing: grayscale;
                    font-smoothing: antialiased;
                }
            }
            .header {
                display: flex;
                justify-content: space-between;
            }
        }

        /* Safari mobile button fixes */
        .styled-buttons {
            button {
                /* Safari mobile button appearance */
                -webkit-appearance: none;
                -webkit-border-radius: 4px;
                border-radius: 4px;
                /* Safari mobile button text */
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
                font-smoothing: antialiased;
            }
        }
    }

    /* Ensure visibility on very small screens */
    @media (max-width: 375px) {
        display: block !important;
        padding: 20px 12px 24px 12px;
    }

    /* Safari-specific mobile support */
    @supports (-webkit-touch-callout: none) {
        @media (max-width: 768px) {
            display: block !important;
        }
    }

    /* Additional mobile breakpoints */
    @media (max-width: 480px) {
        display: block !important;
    }

    /* Force mobile layout on small screens */
    @media (max-width: 767px) {
        display: block !important;
        width: 100vw;
        max-width: 100vw;
        /* Additional Safari mobile properties */
        -webkit-overflow-scrolling: touch;
        -webkit-transform: translateZ(0);
        transform: translateZ(0);
        /* Ensure proper positioning */
        position: fixed !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        /* Safari mobile appearance fixes */
        -webkit-appearance: none;
        -webkit-border-radius: 16px 16px 0 0;
        border-radius: 16px 16px 0 0;
        /* Ensure proper Safari mobile rendering */
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
        -webkit-perspective: 1000px;
        perspective: 1000px;
        /* Safari mobile box model */
        -webkit-box-sizing: border-box;
        box-sizing: border-box;
    }

    /* Safari-specific mobile support */
    @supports (-webkit-touch-callout: none) {
        @media (max-width: 768px) {
            display: block !important;
            /* Safari mobile specific fixes */
            -webkit-transform: translate3d(0, 0, 0);
            transform: translate3d(0, 0, 0);
            -webkit-will-change: transform;
            will-change: transform;
        }
    }

    /* Additional Safari mobile overrides */
    @supports (-webkit-touch-callout: none) {
        @media (max-width: 767px) {
            /* Force bottom sheet layout */
            position: fixed !important;
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            top: auto !important;
            transform: none !important;
            width: 100vw !important;
            max-width: 100vw !important;
            height: auto !important;
            min-height: auto !important;
            border-radius: 16px 16px 0 0 !important;
            border-bottom-left-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
        }
    }

    /* Desktop view styles */
    ${breakpoint.breakTablet`
        display: block;
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 700px;
        max-width: 90vw;
        z-index: 15;
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        padding: 50px 30px;
        animation: none;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        
        /* Override for Safari mobile to force bottom sheet */
        @supports (-webkit-touch-callout: none) {
            @media (max-width: 768px) {
                position: fixed !important;
                top: auto !important;
                bottom: 0 !important;
                left: 0 !important;
                right: 0 !important;
                transform: none !important;
                width: 100% !important;
                max-width: 100% !important;
                height: auto !important;
                min-height: auto !important;
                border-radius: 16px 16px 0 0 !important;
                border-bottom-left-radius: 0 !important;
                border-bottom-right-radius: 0 !important;
                box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1) !important;
                animation: ${slideUp} 0.3s ease-out !important;
                padding: 38px 16px 30px 16px !important;
            }
        }
        
        .text-container {
            display: flex;
            flex-direction: column;
            gap: 20px;
            text-align: center;
            h2 {
                font-size: 24px;
                font-weight: 600;
                margin-bottom: 22px;
            }
            p {
                color: #6e7375;
                font-size: 16px;
                margin-bottom: 22px;
            }
            .styled-buttons {
                display: flex;
                flex-direction: column;
                gap: 14px;
            }
        }
        
        .details-container {
            display: flex;
            flex-direction: column;
            gap: 20px;
            margin-bottom: 30px;
            h2 {
                font-size: 26px;
                font-weight: 700;
                margin-bottom: 20px;
            }
            .details {
                display: flex;
                align-items: center;
                gap: 16px;
                div {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    h1 {
                        font-size: 24px;
                        font-weight: 600;
                    }
                    p {
                        font-size: 12px;
                        color: #6e7375;
                    }
                }
            }
            .header {
            display: flex;
            justify-content: space-between;
        }
        }
        
        .styled-buttons {
            display: flex;
            gap: 24px;
        }
    `}

    /* Large desktop view styles */
    ${breakpoint.breakLarge`
        width: 800px;
        padding: 60px 40px;
        
        .text-container {
            h2 {
                font-size: 28px;
            }
            p {
                font-size: 18px;
            }
        }
        
        .details-container {
            h2 {
                font-size: 30px;
            }
            .details {
                div {
                    h1 {
                        font-size: 28px;
                    }
                    p {
                        font-size: 14px;
                    }
                }
            }
            .header {
                display: flex;
                justify-content: space-between;
            }
        }
    `}
`;

const BorderButton = styled(StyledBorderButton)`
    margin-top: 0px;
    padding: 24px 0px;

    /* Mobile touch improvements */
    @media (max-width: 768px) {
        /* Ensure touch targets are properly sized */
        min-height: 44px;
        /* Safari mobile touch improvements */
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
    }

    /* Desktop button improvements */
    ${breakpoint.breakTablet`
        transition: all 0.2s ease;
        &:hover {
            transform: scale(1.02);
            opacity: 0.9;
        }
    `}
`;

const NormalStyledButton = styled(StyledButton)`
    margin-top: 0px;
    padding: 25px 0px;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
    }

    /* Mobile touch improvements */
    @media (max-width: 768px) {
        /* Ensure touch targets are properly sized */
        min-height: 44px;
        /* Safari mobile touch improvements */
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
    }

    /* Desktop button improvements */
    ${breakpoint.breakTablet`
        transition: all 0.2s ease;
        &:hover:not(:disabled) {
            transform: scale(1.02);
            opacity: 0.9;
        }
    `}
`;

const StyledSuggestionButton = styled.button<StyledSuggestionButtonProps>`
    width: 30px;
    height: 30px;
    padding: 6px;
    border-radius: 50px;
    border: ${(props) =>
        props.isSuggestionClicked ? `1px solid ${theme.colors.time_radio}` : "1px solid #daddde"};
    transition: background-color 0.3s ease;
    cursor: pointer;
    background-color: ${(props) => (props.isSuggestionClicked ? theme.colors.time_radio : "")};

    /* Mobile touch improvements */
    @media (max-width: 768px) {
        /* Safari mobile touch improvements */
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
    }

    div {
        width: 100%;
        height: 100%;
        border-radius: 50px;
        background-color: #fff;
    }
`;

const ScrollBox = styled.div`
    max-height: 20vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 20px;

    /* Safari mobile scrollbar styling */
    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #9ca3af;
    }

    /* Mobile view - use margin instead of gap for Safari compatibility */
    @media (max-width: 768px) {
        gap: 0;
        /* Safari mobile scrollbar fixes */
        -webkit-overflow-scrolling: touch;
        -webkit-scrollbar-width: thin;
        -webkit-scrollbar-color: #d1d5db transparent;

        .details {
            margin-bottom: 20px;
            &:last-child {
                margin-bottom: 0;
            }
        }
    }
`;

const NoSuggestions = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 20px;
    text-align: center;

    p {
        color: #6e7375;
        font-size: 16px;
        margin: 0;
    }
`;

const ReviewBadge = styled.span`
    display: inline-block;
    margin-left: 8px;
    padding: 1px 8px;
    border-radius: 10px;
    /* Red on red: the flavour's own red for the text, and the same red faded to
       a tint behind it, so the pairing stays on-brand for both flavours instead
       of hard-coding a second pink. The trailing hex pair is the alpha. */
    background: ${theme.colors.red}1F;
    color: ${theme.colors.red};
    font-size: 11px;
    font-weight: 600;
    vertical-align: middle;
    white-space: nowrap;
`;
